import { LayoutTemplate, RefreshCw, MousePointerClick, ShieldCheck, ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useInquiry } from "@/components/InquiryModal";
import useTitle from "@/lib/useTitle";
import brand from "@/config/brand";

const SERVICES = [
  {
    id: "new-website",
    icon: LayoutTemplate,
    title: "New Website Design",
    preset: "New Website",
    intro:
      "A beautiful website is often the first impression your business makes — and first impressions decide whether a visitor becomes a customer. We design professional, mobile-friendly websites that reflect the quality of your work and make it effortless for customers to reach you.",
    who: "Perfect for new businesses, established businesses without a website, or anyone whose online presence doesn't match the quality of their work.",
    bullets: [
      "Custom professional design — never a cookie-cutter template",
      "Mobile-responsive layout that looks great on every device",
      "Contact and quote-request forms that capture leads",
      "Basic on-page SEO setup so customers can find you",
      "Launch assistance and domain connection handled for you",
    ],
  },
  {
    id: "redesign",
    icon: RefreshCw,
    title: "Website Redesign",
    preset: "Redesign",
    intro:
      "An outdated website can quietly cost you customers. We'll give your existing site a fresh, modern look with improved navigation, updated design, and a better experience across every device — while keeping what already works.",
    who: "For businesses whose current website feels dated, loads poorly on phones, or no longer represents the business they've become. Existing websites can be evaluated for redesign, enhancement, or migration depending on their current platform.",
    bullets: [
      "A fresh, modern design that matches your business today",
      "Improved navigation and page structure",
      "A better experience on phones, tablets, and desktops",
      "Content carried over and refined from your current site",
      "An honest assessment: refresh, enhance, or rebuild",
    ],
  },
  {
    id: "custom-features",
    icon: MousePointerClick,
    title: "Custom Features & Interactive Tools",
    preset: "Custom Feature",
    intro:
      "Go beyond a basic brochure website with interactive tools that engage visitors and win you business — like an instant-estimate calculator that turns curious visitors into qualified inquiries while you sleep.",
    who: "For businesses that want their website to do more: answer questions, qualify leads, provide instant estimates, or guide customers to the right product or service.",
    bullets: [
      "Instant estimate calculators tailored to your services",
      "Interactive quizzes and product finders",
      "Quote-request and inquiry forms built for conversion",
      "Booking and scheduling functionality",
      "Custom integrations quoted for your exact needs",
    ],
  },
  {
    id: "managed-care",
    icon: ShieldCheck,
    title: "Managed Hosting & Website Care",
    preset: "Other",
    intro:
      "A website isn't a one-time project — it needs a home, ongoing technical upkeep, and someone to call when you need a change. Our managed hosting and care plan keeps your website running smoothly so you can focus on running your business.",
    who: "For every website we build and host — and for business owners who never want to think about servers, security certificates, or software updates.",
    bullets: [
      "Reliable website hosting",
      "SSL support and security basics",
      "Ongoing technical maintenance",
      "Minor content updates when you need them",
      "A real person to call when something needs attention",
    ],
  },
];

const Services = () => {
  const { open } = useInquiry();
  useTitle(
    "Services & Quotes",
    "Website design, redesigns, custom interactive tools, and managed hosting & care for small businesses — every project personally quoted."
  );

  return (
    <main data-testid="services-page" className="bg-cream pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-champagne/30 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-turquoise-deep mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-turquoise inline-block" />
              Services & Quotes
            </p>
            <h1 className="font-serif font-medium tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              Everything Your Website Needs.{" "}
              <span className="italic text-turquoise">Handled.</span>
            </h1>
            <p className="mt-6 text-lg font-sans text-taupe leading-relaxed max-w-2xl">
              From brand-new websites to redesigns, interactive tools, and ongoing care — every
              project is tailored to your business and quoted personally. No fixed packages, no
              surprises.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Service blocks */}
      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          data-testid={`service-detail-${s.id}`}
          className={`py-16 md:py-24 ${i % 2 === 0 ? "bg-cream" : "bg-cream-dark border-y border-taupe/10"}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <Reveal className="lg:col-span-7">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-turquoise/10 border border-turquoise/30 flex items-center justify-center shrink-0">
                    <s.icon size={24} className="text-turquoise-deep" strokeWidth={1.75} />
                  </div>
                  <h2 className="font-serif font-medium tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight">
                    {s.title}
                  </h2>
                </div>
                <p className="font-sans text-base text-charcoal/80 leading-relaxed">{s.intro}</p>
                <p className="mt-4 font-sans text-sm text-taupe leading-relaxed border-l-2 border-turquoise/40 pl-4">
                  {s.who}
                </p>
                <button
                  data-testid={`service-detail-quote-${s.id}`}
                  onClick={() => open(s.preset)}
                  className="group mt-8 inline-flex items-center gap-2 bg-turquoise text-charcoal hover:bg-turquoise-dark px-8 py-3.5 rounded-full font-sans font-semibold text-sm shadow-[0_14px_28px_-10px_rgba(0,175,163,0.45)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5"
                >
                  Request a Quote
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </Reveal>
              <Reveal delay={0.12} className="lg:col-span-5">
                <div className="bg-white/50 border border-taupe/15 rounded-2xl p-8">
                  <p className="text-xs font-sans uppercase tracking-[0.2em] font-semibold text-turquoise-deep mb-5">
                    What's Included
                  </p>
                  <ul className="space-y-3.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 font-sans text-sm text-charcoal/80">
                        <Check size={16} className="text-turquoise-deep shrink-0 mt-0.5" strokeWidth={2.5} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      {/* Hosting note + CTA */}
      <section data-testid="services-cta-band" className="relative bg-charcoal py-20 md:py-28 overflow-hidden">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-turquoise/10 blur-[150px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif font-medium tracking-tight text-cream text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Every project begins with a <span className="italic text-turquoise">conversation.</span>
            </h2>
            <p className="mt-6 font-sans text-sm sm:text-base text-champagne/60 leading-relaxed max-w-2xl mx-auto border-l-2 sm:border-l-0 border-turquoise/50 pl-5 sm:pl-0 text-left sm:text-center">
              {brand.hostingStatement}
            </p>
            <Link
              data-testid="services-page-quote-button"
              to="/contact"
              className="group mt-10 inline-flex items-center gap-2 bg-turquoise text-charcoal hover:bg-turquoise-dark px-10 py-4 rounded-full font-sans font-semibold text-base shadow-[0_20px_40px_-12px_rgba(0,175,163,0.5)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5"
            >
              Request a Quote
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Services;
